import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { FaBell, FaThumbtack, FaStar, FaRegStar, FaLock } from 'react-icons/fa';
import Dashboard from './Dashboard';
import AdminPanel from './AdminPanel';
import ChannelSidebar from '@/components/channels/ChannelSidebar';
import MessageInput from '@/components/messages/MessageInput';
import MessageSearch from '@/components/messages/MessageSearch';
import VoiceChannel from '@/components/voice/VoiceChannel';
import ConnectionStatus from '@/components/common/ConnectionStatus';
import OnlineUsers from '@/components/channels/OnlineUsers';
import ChannelNotificationSettings from '@/components/settings/ChannelNotificationSettings';
import PinnedMessagesPanel from '@/components/messages/PinnedMessagesPanel';
import api from '@/services/api';
import socketService from '@/services/socket';
import { refreshAccessToken } from '@/services/tokenRefresh';
import pushNotificationsService from '@/services/pushNotifications';
import { announcementEventEmitter } from '@/services/announcementEventEmitter';
import { useAutoStatus } from '@/hooks/useAutoStatus';
import { useFavoriteChannels } from '@/hooks/useFavoriteChannels';
import { useToast } from '@/components/common/ToastProvider';
import { uiStateManager } from '@/utils/uiStateManager';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useVoiceCall } from '@/contexts/VoiceCallContext';

// Code splitting for MessageList
const MessageList = lazy(() => import('@/components/messages/MessageList'));

export interface ChannelData {
  id: string;
  name: string;
  description?: string;
  isPrivate?: boolean;
  isVoice?: boolean;
  position?: number;
  [key: string]: any;
}

interface ChatProps {
  initialView?: 'channel' | 'dashboard' | 'admin';
}

export default function Chat({ initialView }: ChatProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<'channel' | 'dashboard' | 'admin'>(() => {
    if (initialView) return initialView;
    if (window.location.pathname === '/admin' || searchParams.get('admin') !== null) return 'admin';
    if (searchParams.get('dashboard') !== null) return 'dashboard';
    return 'channel';
  });

  const [currentChannelId, setCurrentChannelId] = useState<string | null>(null);
  const [currentChannel, setCurrentChannel] = useState<ChannelData | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [onlineUsersOpen, setOnlineUsersOpen] = useState(true);
  const [notificationSettingsOpen, setNotificationSettingsOpen] = useState(false);
  const [pinnedMessagesOpen, setPinnedMessagesOpen] = useState(false);
  const [jumpToMessageId, setJumpToMessageId] = useState<string | null>(null);
  const HIGHLIGHT_DURATION_MS = 6000;
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    () => !!localStorage.getItem('accessToken'),
  );
  const toast = useToast();
  const { favoriteChannels, toggleFavorite: rawToggleFavorite } = useFavoriteChannels();
  const { isInCall } = useVoiceCall();
  const { isMobile } = useBreakpoint();

  // Enable automatic status detection
  useAutoStatus();

  const handleViewChange = useCallback((newView: 'channel' | 'dashboard' | 'admin') => {
    setViewMode(newView);
    setSidebarOpen(false);
    if (newView === 'admin') {
      setCurrentChannelId(null);
      setCurrentChannel(null);
      uiStateManager.setCurrentChannelId(null);
      navigate('/admin', { replace: false });
    } else if (newView === 'dashboard') {
      setCurrentChannelId(null);
      setCurrentChannel(null);
      uiStateManager.setCurrentChannelId(null);
      navigate('/chat?dashboard=1', { replace: false });
    }
  }, [navigate]);

  const handleChannelSelect = useCallback(async (channelId: string, updateUrl = true) => {
    if (!channelId) {
      handleViewChange('dashboard');
      return;
    }
    setViewMode('channel');
    setCurrentChannelId(channelId);
    uiStateManager.setCurrentChannelId(channelId);
    setSidebarOpen(false);
    if (updateUrl) {
      navigate(`/chat?channel=${channelId}`, { replace: false });
    }
    try {
      const response = await api.get(`/channels/${channelId}`);
      setCurrentChannel(response.data.channel || response.data);
    } catch (error) {
      console.error('Error fetching channel:', error);
    }
  }, [navigate, handleViewChange]);

  // Handle route and search parameters seamlessly without page reloads
  useEffect(() => {
    if (location.pathname === '/admin' || searchParams.get('admin') !== null) {
      setViewMode('admin');
      setCurrentChannelId(null);
      setCurrentChannel(null);
      uiStateManager.setCurrentChannelId(null);
    } else if (searchParams.get('dashboard') !== null) {
      setViewMode('dashboard');
      setCurrentChannelId(null);
      setCurrentChannel(null);
      uiStateManager.setCurrentChannelId(null);
    } else {
      const channelId = searchParams.get('channel');
      if (channelId) {
        setViewMode('channel');
        handleChannelSelect(channelId, false);
      } else {
        const savedChannelId = uiStateManager.getCurrentChannelId();
        if (savedChannelId) {
          setViewMode('channel');
          handleChannelSelect(savedChannelId, false);
        } else {
          setViewMode('dashboard');
        }
      }
    }

    // Restore panel states from UI state
    const savedPanelStates = uiStateManager.getPanelStates();
    setOnlineUsersOpen(savedPanelStates.onlineUsersOpen);
    setPinnedMessagesOpen(savedPanelStates.pinnedMessagesOpen);
    setNotificationSettingsOpen(savedPanelStates.notificationSettingsOpen);

    // Restore state after SW update reload
    const savedPath = sessionStorage.getItem('sw_update_return_path');
    if (savedPath && savedPath !== window.location.pathname + window.location.search) {
      sessionStorage.removeItem('sw_update_return_path');
      navigate(savedPath, { replace: true });
    }
  }, [searchParams, location.pathname, handleChannelSelect, navigate]);

  useEffect(() => {
    let mounted = true;

    const initConnection = async () => {
      let token = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');

      if (!token && refreshToken) {
        try {
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            token = localStorage.getItem('accessToken');
          }
        } catch (err) {
          console.error('[Chat] Silent token refresh failed:', err);
        }
      }

      if (!token) {
        navigate('/login');
        return;
      }

      if (!mounted) return;
      setIsAuthenticated(true);
      socketService.connect();

      pushNotificationsService.init().catch((error) => {
        console.error('Failed to initialize push notifications:', error);
      });
    };

    initConnection();

    return () => {
      mounted = false;
    };
  }, []);

  // Save UI state when panels change
  useEffect(() => {
    uiStateManager.setPanelStates({
      pinnedMessagesOpen,
      onlineUsersOpen,
      notificationSettingsOpen,
    });
  }, [pinnedMessagesOpen, onlineUsersOpen, notificationSettingsOpen]);

  // Handle announcements and security force-logouts
  useEffect(() => {
    const handleNewAnnouncement = (announcement: any) => {
      toast.info(announcement.title || 'New announcement');
      announcementEventEmitter.emit(announcement);
    };

    const handleForceLogout = (data: { reason?: string }) => {
      toast.error(data?.reason || 'Your session was revoked. Please sign in again.');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      socketService.disconnect();
      navigate('/login');
    };

    socketService.on('admin:announcement', handleNewAnnouncement);
    socketService.on('auth:force-logout', handleForceLogout);

    return () => {
      socketService.off('admin:announcement', handleNewAnnouncement);
      socketService.off('auth:force-logout', handleForceLogout);
    };
  }, [toast, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    socketService.disconnect();
    navigate('/login');
  };

  const toggleFavorite = async (channelId: string) => {
    const isCurrentlyFavorited = favoriteChannels.has(channelId);
    try {
      await rawToggleFavorite(channelId);
      toast.success(
        isCurrentlyFavorited ? 'Channel removed from favorites' : 'Channel added to favorites',
      );
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update favorite');
    }
  };

  const handleToggleFavorite = async () => {
    if (!currentChannelId) return;
    await toggleFavorite(currentChannelId);
  };

  return (
    <div
      className={`flex h-screen bg-brand-surface/90 dark:bg-brand-background/90 glass-effect overflow-hidden ${isInCall ? 'pb-16' : ''}`}
    >
      <ConnectionStatus />

      {isAuthenticated && (
        <>
          {/* Mobile sidebar overlay */}
          {sidebarOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Left sidebar - persistent across all views */}
          <div
            className={`
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            ${isMobile ? 'fixed z-50' : 'relative translate-x-0'}
            w-64 border-r border-brand-outline-variant dark:border-brand-outline flex flex-col
            bg-brand-surface/90 dark:bg-brand-background/90 glass-effect
            transition-transform duration-300 ease-in-out
            h-full flex-shrink-0
          `}
          >
            <div className="flex-1 overflow-hidden">
              <ChannelSidebar
                currentChannelId={currentChannelId}
                onChannelSelect={(id) => handleChannelSelect(id, true)}
                onDashboardClick={() => handleViewChange('dashboard')}
                onAdminClick={() => handleViewChange('admin')}
                currentView={viewMode}
                favoriteChannels={favoriteChannels}
                onToggleFavorite={toggleFavorite}
              />
            </div>
          </div>
        </>
      )}

      {/* Main content area */}
      {isAuthenticated && (
        <div className="flex-1 flex min-w-0 overflow-hidden">
          <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
            {viewMode === 'admin' ? (
              <AdminPanel
                embedded={true}
                onExit={() => handleViewChange('dashboard')}
                onOpenSidebar={() => setSidebarOpen(!sidebarOpen)}
              />
            ) : viewMode === 'channel' && currentChannelId && currentChannel ? (
              <>
                <div className="h-14 md:h-16 border-b border-brand-outline-variant dark:border-brand-outline flex items-center justify-between px-3 md:px-6 flex-shrink-0">
                  <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    {/* Mobile menu button */}
                    {isMobile && (
                      <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className="p-2.5 rounded-full text-brand-outline hover:text-white hover:bg-white/10 active:scale-95 transition-all focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none"
                        aria-label="Open sidebar menu"
                        aria-expanded={sidebarOpen}
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 6h16M4 12h16M4 18h16"
                          />
                        </svg>
                      </button>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h1 className="text-lg md:text-xl font-bold text-brand-on-background dark:text-[#e0e7ff] truncate">
                          {currentChannel.isVoice ? '🔊' : '#'} {currentChannel.name}
                        </h1>
                        {currentChannel.isVoice && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 shadow-sm"
                            title="End-to-End Encrypted Voice (WebRTC DTLS-SRTP)"
                          >
                            <FaLock size={9} className="text-emerald-400" />
                            <span>Encrypted Voice</span>
                          </span>
                        )}
                      </div>
                      {currentChannel.description && (
                        <p className="text-xs md:text-sm text-brand-outline dark:text-[#c4b5fd] truncate hidden sm:block">
                          {currentChannel.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
                    {/* Favorite toggle button */}
                    <button
                      onClick={handleToggleFavorite}
                      className="p-2.5 rounded-full text-brand-outline hover:text-white hover:bg-white/10 active:scale-95 transition-all focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none cursor-pointer"
                      title={
                        favoriteChannels.has(currentChannelId)
                          ? 'Remove from favorites'
                          : 'Add to favorites'
                      }
                      aria-label={
                        favoriteChannels.has(currentChannelId)
                          ? 'Remove from favorites'
                          : 'Add to favorites'
                      }
                    >
                      {favoriteChannels.has(currentChannelId) ? (
                        <FaStar size={18} className="text-yellow-400" />
                      ) : (
                        <FaRegStar size={18} />
                      )}
                    </button>
                    {/* Message search for text channels */}
                    {!currentChannel.isVoice && (
                      <MessageSearch
                        channelId={currentChannelId}
                        onSelectMessage={(messageId) => {
                          setJumpToMessageId(messageId);
                          setTimeout(() => setJumpToMessageId(null), 5000);
                        }}
                      />
                    )}
                    {/* Pinned messages for text channels */}
                    {!currentChannel.isVoice && (
                      <button
                        onClick={() => setPinnedMessagesOpen(!pinnedMessagesOpen)}
                        className="p-2.5 rounded-full text-brand-outline hover:text-white hover:bg-white/10 active:scale-95 transition-all focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none cursor-pointer"
                        aria-label={
                          pinnedMessagesOpen ? 'Hide pinned messages' : 'Show pinned messages'
                        }
                        aria-expanded={pinnedMessagesOpen}
                      >
                        <FaThumbtack size={16} />
                      </button>
                    )}
                    {/* Notification settings for text channels */}
                    {!currentChannel.isVoice && (
                      <button
                        onClick={() => setNotificationSettingsOpen(true)}
                        className="p-2.5 rounded-full text-brand-outline hover:text-white hover:bg-white/10 active:scale-95 transition-all focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none cursor-pointer"
                        title="Notification settings"
                        aria-label="Notification settings"
                      >
                        <FaBell size={16} />
                      </button>
                    )}
                    {/* Toggle online users on desktop */}
                    <button
                      onClick={() => setOnlineUsersOpen(!onlineUsersOpen)}
                      className="hidden lg:block p-2.5 rounded-full text-brand-outline hover:text-white hover:bg-white/10 active:scale-95 transition-all focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none cursor-pointer"
                      title={onlineUsersOpen ? 'Hide online users' : 'Show online users'}
                      aria-label={onlineUsersOpen ? 'Hide online users' : 'Show online users'}
                      aria-expanded={onlineUsersOpen}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="btn btn-outlined text-xs sm:text-sm px-4 py-1.5 rounded-full font-medium border border-brand-outline-variant/80 text-brand-on-surface hover:bg-white/10 hover:text-white active:scale-95 transition-all cursor-pointer"
                      aria-label="Logout"
                    >
                      Logout
                    </button>
                  </div>
                </div>

                {currentChannel.isVoice ? (
                  <VoiceChannel channelId={currentChannelId} />
                ) : (
                  <>
                    <Suspense
                      fallback={
                        <div role="status" aria-live="polite" className="flex-1 flex items-center justify-center">
                          <div className="animate-pulse">Loading messages...</div>
                        </div>
                      }
                    >
                      <MessageList
                        channelId={currentChannelId}
                        jumpToMessageId={jumpToMessageId || undefined}
                      />
                    </Suspense>
                    <MessageInput channelId={currentChannelId} />
                  </>
                )}
              </>
            ) : (
              <div className="flex-1 overflow-auto">
                {/* Mobile menu button when no channel selected */}
                <div className="md:hidden fixed top-4 left-4 z-10">
                  <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="px-4 py-2 bg-primary-600 text-brand-on-primary rounded-lg shadow-lg focus-visible:ring-2 focus-visible:ring-white focus:outline-none"
                    aria-label="Open sidebar menu"
                    aria-expanded={sidebarOpen}
                  >
                    Open Menu
                  </button>
                </div>
                <Dashboard onChannelSelect={(id) => handleChannelSelect(id, true)} />
              </div>
            )}
          </div>

          {/* Right sidebar - Online users (desktop only, for channels only) */}
          {viewMode === 'channel' && currentChannelId && onlineUsersOpen && (
            <div className="hidden lg:block w-64 border-l border-brand-outline-variant dark:border-brand-outline bg-brand-surface/90 dark:bg-brand-background/90 glass-effect">
              <OnlineUsers channelId={currentChannelId} />
            </div>
          )}
        </div>
      )}

      {/* Notification Settings Modal */}
      {viewMode === 'channel' && currentChannelId && currentChannel && !currentChannel.isVoice && (
        <ChannelNotificationSettings
          isOpen={notificationSettingsOpen}
          onClose={() => setNotificationSettingsOpen(false)}
          channelId={currentChannelId}
          channelName={currentChannel.name}
          isVoice={false}
        />
      )}

      {/* Pinned Messages Panel */}
      {viewMode === 'channel' && currentChannelId && pinnedMessagesOpen && (
        <PinnedMessagesPanel
          channelId={currentChannelId}
          onClose={() => setPinnedMessagesOpen(false)}
          onMessageClick={(messageId) => {
            const messageElement = document.querySelector(
              `#message-list-container [data-message-id="${messageId}"]`,
            );
            if (messageElement) {
              messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              messageElement.classList.add('ring-2', 'ring-primary-500', 'dark:ring-primary-400');
              setTimeout(() => {
                messageElement.classList.remove(
                  'ring-2',
                  'ring-primary-500',
                  'dark:ring-primary-400',
                );
              }, HIGHLIGHT_DURATION_MS);
            }
          }}
        />
      )}
    </div>
  );
}
